export type EntitlementValue = boolean | number | string;
export type FeatureCategory = 'CORE_UNIVERSAL' | 'PLAN_FEATURE' | 'CAPACITY';
export type FeatureValueType = 'BOOLEAN' | 'INTEGER' | 'STRING';
export const immutableFeatureFields = ['code', 'value_type', 'category'] as const;

export interface PlanRecord {
  id: string;
  product_id: string;
  product_code: string;
  code: string;
  name: string;
  description: string | null;
  display_order: number;
  active: boolean;
  entitlements: Record<string, EntitlementValue>;
  plan_default_limits?: {
    max_extensions: number;
    max_concurrent_calls: number;
    max_tenants: number;
  };
}

export interface FeatureUsage {
  plan_code: string;
  plan_name: string;
  value: EntitlementValue;
  display_order: number;
}

export interface FeatureRecord {
  id: string;
  product_id: string;
  product_code: string;
  code: string;
  name: string;
  description: string | null;
  value_type: FeatureValueType;
  default_value: EntitlementValue;
  category: FeatureCategory;
  coming_soon: boolean;
  display_order: number;
  active: boolean;
  plan_usage: FeatureUsage[];
}

export interface MatrixChange {
  planCode: string;
  planName: string;
  featureCode: string;
  featureName: string;
  before: EntitlementValue;
  after: EntitlementValue;
}

export const coreComparisonFeatures = [
  'Overview',
  'CDR Records',
  'Call Reports',
  'Prefix Rates',
  'PIN & Destination Management',
  'Import Centre',
] as const;

export function matrixChanges(
  plans: PlanRecord[],
  features: FeatureRecord[],
  draft: Record<string, Record<string, EntitlementValue>>,
): MatrixChange[] {
  const editable = new Map(
    features
      .filter((feature) => feature.category !== 'CORE_UNIVERSAL')
      .map((feature) => [feature.code, feature]),
  );
  const changes: MatrixChange[] = [];
  for (const plan of plans) {
    for (const [featureCode, after] of Object.entries(draft[plan.code] ?? {})) {
      const feature = editable.get(featureCode);
      if (!feature) continue;
      const before = plan.entitlements[featureCode] ?? feature.default_value;
      if (JSON.stringify(before) !== JSON.stringify(after))
        changes.push({
          planCode: plan.code,
          planName: plan.name,
          featureCode,
          featureName: feature.name,
          before,
          after,
        });
    }
  }
  return changes;
}

export function filterFeatures(
  features: FeatureRecord[],
  query: string,
  valueType: string,
  category: string,
  status: string,
): FeatureRecord[] {
  const needle = query.trim().toLowerCase();
  return features.filter(
    (feature) =>
      (!needle ||
        feature.name.toLowerCase().includes(needle) ||
        feature.code.toLowerCase().includes(needle) ||
        (feature.description ?? '').toLowerCase().includes(needle)) &&
      (!valueType || feature.value_type === valueType) &&
      (!category || feature.category === category) &&
      (!status || (status === 'ACTIVE' ? feature.active : !feature.active)),
  );
}

export function planCapacityLabel(value: EntitlementValue | undefined): string {
  const capacity = typeof value === 'number' ? value : 1;
  return `${capacity} ${capacity === 1 ? 'PBX' : 'PBXs'}`;
}

export function displayEntitlement(value: EntitlementValue): string {
  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';
  return String(value);
}

export function protectedCorePabxState(
  plan: PlanRecord,
  featureCode: string,
): {
  observed: 'ON' | 'OFF' | 'MISSING' | 'INVALID';
  discrepancy: boolean;
} {
  const actual = plan.entitlements[featureCode];
  const expected = featureCode === 'multi_tenant' ? plan.code === 'enterprise' : true;
  const observed =
    actual === undefined
      ? 'MISSING'
      : typeof actual !== 'boolean'
        ? 'INVALID'
        : actual
          ? 'ON'
          : 'OFF';
  return { observed, discrepancy: actual !== expected };
}
